import { z } from "zod";
import { SALEOR_CHANNEL_SLUGS } from "@/config/currencies";
import { PAYTREE_METHODS } from "@/config/paytree";
import definitions from "./campaign-definitions.json";

const copySchema = z.object({
	paymentNotice: z.string().min(1),
	banner: z.string().min(1),
	bannerLabel: z.string().min(1),
	bannerDeadline: z.string().min(1),
	bannerCountdown: z.string().min(1),
});
const mailContentSchema = z.object({
	subject: z.string().min(1),
	html: z.string().min(1),
	text: z.string().min(1),
});
const localizedIds = z.object({
	de: z.string().regex(/^[a-z0-9-]{1,120}$/),
	en: z.string().regex(/^[a-z0-9-]{1,120}$/),
});
const paymentMethodSchema = z.union([
	z.literal("vorkasse"),
	z.literal("crypto"),
	z.enum(PAYTREE_METHODS.map(({ id }) => id)),
]);
const campaignSchema = z.object({
	id: z.string().regex(/^[a-z0-9-]{1,120}$/),
	codes: z.array(z.string().trim().min(1)).min(1),
	percent: z.number().positive().max(100),
	startsAt: z.iso.datetime({ offset: true }),
	endsAt: z.iso.datetime({ offset: true }),
	channels: z.array(z.enum(SALEOR_CHANNEL_SLUGS)).min(1),
	allowedPaymentMethods: z
		.array(paymentMethodSchema)
		.min(1)
		.refine((methods) => methods.includes("vorkasse"), "vorkasse_required"),
	copy: z.object({ de: copySchema, en: copySchema }),
	audience: z.object({
		cartLookbackDays: z.number().int().nonnegative(),
		recentBuyerDays: z.number().int().nonnegative(),
	}),
	mail: z
		.object({
			waves: z.record(
				z.string().min(1),
				z.object({
					ids: localizedIds,
					content: z.object({ de: mailContentSchema, en: mailContentSchema }),
					previousWave: z.string().min(1).optional(),
				}),
			),
		})
		.optional(),
});

export type CampaignDefinition = z.infer<typeof campaignSchema>;
export type CampaignCopy = z.infer<typeof copySchema>;
export type CampaignPaymentIdentity = { id: string; code: string };

/** The browser, server guards and operator tools consume the same validated policy. */
export function validateCampaignDefinitions(value: unknown): CampaignDefinition[] {
	const parsed = z.array(campaignSchema).safeParse(value);
	if (!parsed.success) throw new Error("invalid_campaign_configuration");
	const campaigns = parsed.data;
	const ids = new Set<string>();
	const mailIds = new Set<string>();
	for (const campaign of campaigns) {
		if (ids.has(campaign.id) || Date.parse(campaign.startsAt) >= Date.parse(campaign.endsAt))
			throw new Error("invalid_campaign_configuration");
		ids.add(campaign.id);
		campaign.codes = campaign.codes.map((code) => code.toUpperCase());
		if (new Set(campaign.codes).size !== campaign.codes.length) throw new Error("duplicate_campaign_code");
		for (const [wave, definition] of Object.entries(campaign.mail?.waves ?? {})) {
			if (
				definition.previousWave &&
				(!campaign.mail?.waves[definition.previousWave] || definition.previousWave === wave)
			)
				throw new Error("invalid_campaign_previous_wave");
			for (const id of Object.values(definition.ids)) {
				if (mailIds.has(id)) throw new Error("duplicate_campaign_mail_id");
				mailIds.add(id);
			}
		}
	}
	for (let i = 0; i < campaigns.length; i++)
		for (let j = i + 1; j < campaigns.length; j++) {
			const a = campaigns[i],
				b = campaigns[j];
			if (
				a.codes.some((code) => b.codes.includes(code)) &&
				a.channels.some((channel) => b.channels.includes(channel)) &&
				Date.parse(a.startsAt) < Date.parse(b.endsAt) &&
				Date.parse(b.startsAt) < Date.parse(a.endsAt)
			)
				throw new Error("overlapping_campaign_code");
		}
	return campaigns;
}

/** Empty by default. Adding a campaign is a deliberate configuration/release change. */
export const CAMPAIGNS: readonly CampaignDefinition[] = validateCampaignDefinitions(definitions);
export const CAMPAIGN_PAYMENT_METHOD_RESTRICTED = "CAMPAIGN_PAYMENT_METHOD_RESTRICTED";

/** Start inclusive, end exclusive, in absolute time (independent of local DST). */
export function isCampaignActive(
	campaign: CampaignDefinition,
	now: Date = new Date(),
	channel = "standard",
): boolean {
	return (
		campaign.channels.some((configured) => configured === channel) &&
		now.getTime() >= Date.parse(campaign.startsAt) &&
		now.getTime() < Date.parse(campaign.endsAt)
	);
}

export function activeCampaigns(now: Date = new Date(), channel = "standard", campaigns = CAMPAIGNS) {
	return campaigns.filter((campaign) => isCampaignActive(campaign, now, channel));
}

export function campaignForVoucher(
	voucherCode: unknown,
	now: Date = new Date(),
	channel = "standard",
	campaigns = CAMPAIGNS,
) {
	if (typeof voucherCode !== "string") return undefined;
	const code = voucherCode.trim().toUpperCase();
	return activeCampaigns(now, channel, campaigns).find((campaign) => campaign.codes.includes(code));
}

export function getCampaignCopy(campaign: CampaignDefinition, locale: string): CampaignCopy {
	return campaign.copy[locale === "de" ? "de" : "en"];
}

export function isPaymentMethodAllowed(
	method: unknown,
	voucherCode: unknown,
	now: Date = new Date(),
	channel = "standard",
	campaigns = CAMPAIGNS,
): boolean {
	const campaign = campaignForVoucher(voucherCode, now, channel, campaigns);
	return !campaign || campaign.allowedPaymentMethods.some((allowed) => allowed === method);
}

export function getCampaignPaymentRestriction(
	method: unknown,
	voucherCode: unknown,
	now: Date = new Date(),
	channel = "standard",
) {
	const campaign = campaignForVoucher(voucherCode, now, channel);
	return !campaign || campaign.allowedPaymentMethods.some((allowed) => allowed === method)
		? null
		: {
				code: CAMPAIGN_PAYMENT_METHOD_RESTRICTED,
				message: getCampaignCopy(campaign, "en").paymentNotice,
				campaign: {
					id: campaign.id,
					code: campaign.codes.find((code) => code === String(voucherCode).trim().toUpperCase())!,
				},
			};
}

export function formatCampaignCountdown(seconds: number, locale: string): string {
	const remaining = Math.max(0, Math.ceil(seconds));
	const days = remaining > 86_400 ? Math.floor(remaining / 86_400) : 0;
	const hours = Math.floor((remaining - days * 86_400) / 3600);
	const minutes = Math.floor((remaining % 3600) / 60);
	const clock = [hours, minutes, remaining % 60].map((value) => String(value).padStart(2, "0")).join(":");
	if (!days) return clock;
	const unit = locale === "de" ? (days === 1 ? "Tag" : "Tage") : days === 1 ? "day" : "days";
	return `${days} ${unit} ${clock}`;
}
